<template>
  <tabs/>
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters"/>
    <add-fields-pane v-model="visibleColumns" :fields="columns"/>

    <action
      :icon="AddIcon"
      :label="$t('pages.driver.pointList.modal.new.buttonText')"
      @click="pointEntryModalOpen = true"
    />

    <action
      :icon="AddIcon"
      :label="$t('pages.driver.pointList.modal.buyReward.buyRewardButtonText')"
      @click="buyPointRewardModalOpen = true"
    />

    <export
      url="/point_ledgers/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <point-modal
    v-model:open="pointEntryModalOpen"
    v-model:point-entry="editPointEntry"
    :driver="modelValue"
    @saved="refetch()"
  />
  <buy-point-reward-modal
    v-model:open="buyPointRewardModalOpen"
    :driver="modelValue"
    @bought="refetch()"
  />

  <div class="ml-12">
    <div class="table">
      <data-table
        class="mb-auto"
        v-model:sort-by="sortKey"
        v-model:sort-direction="sortDirection"
        :busy="loading"
        :items="records"
        :columns="columns"
        :visible-columns="visibleColumns"
        :actions="actions"
        :rowClick="(item) => {
          if (item.interestArea) {
            router.push({
              name: 'interest-zone-details',
              params: { interestZoneId: item.interestArea.id },
            });
          }
          return undefined;
        }"
      />
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total"/>
</template>

<script lang="tsx" setup>
import { ref, reactive, computed, unref } from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Export from '@components/utils/toolbar/panes/Export.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Action from '@components/utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg?component';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, enumOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import { format } from 'date-fns';
import type { Filter } from '@utils/filter/Filter.vue';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type {
  Driver,
  HydraCollection,
  DriverComment,
  DriverCommentType,
  DriverCommentAttachment,
  PointLedger, Company
} from '@/entities/Entities';

import { useFetchAction } from '@hooks/fetch';
import { useNotify } from '@utils/notifications';
import EditCommentModal from '@pages/drivers/driver/CommentModals/EditCommentModal.vue';
import { useDelete } from '@hooks/api';
import PointModal from '@pages/drivers/driver/PointModal.vue';
import { useQueryRef } from '@/hooks/url';
import { useTableFields } from '@hooks/table-configuration';
import { useRouter } from 'vue-router';
import BuyPointRewardModal from '@pages/drivers/driver/PointRewardModal/BuyPointRewardModal.vue';

interface Props {
  modelValue: Driver | null;
}

const props = defineProps<Props>();
const router = useRouter();

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);
const sortKey = useQueryRef<string | undefined>('sortKey', 'timestamp');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'DESC');

const pointEntryModalOpen = ref(false);
const buyPointRewardModalOpen = ref(false);

const editPointEntry = ref<PointLedger | undefined>(undefined);

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.pointsFromDriver', {
    driver: props.modelValue?.user?.fullName,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  driver: props.modelValue?.id ?? '',
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const editableTypes = ['admin', 'loyalty'];

const [deletePointEntry, { loading: deleting }] = useDelete();

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const actions = [
  {
    title: t('pages.driver.pointList.actions.edit'),
    action: (item: PointLedger) => {
      editPointEntry.value = { ...item };
      pointEntryModalOpen.value = true;
    },
    condition: (item: PointLedger) => editableTypes.includes(item.type),
  },
  {
    title: t('pages.driver.pointList.actions.delete'),
    action: async (item: PointLedger) => {
      if (await deletePointEntry(`/point_ledgers/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
    condition: (item: PointLedger) => editableTypes.includes(item.type),
  },
];

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    group: t('pages.driver.pointList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    visible: false,
    sortable: false,
  },
  {
    key: 'timestamp',
    title: t('date'),
    group: t('pages.driver.pointList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.driver.pointList.filterGroups.general'),
      options: {
        label: t('date'),
      },
    },
  },
  {
    key: 'amount',
    title: t('pointLedger.amount'),
    group: t('pages.driver.pointList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.driver.pointList.filterGroups.general'),
      options: {
        label: t('pointLedger.amount'),
      },
    },
  },
  {
    key: 'type',
    csvTranslations: {
      zone: t('pointLedger.type.zone'),
      zone_standing: t('pointLedger.type.zone_standing'),
      longday: t('pointLedger.type.longday'),
      admin: t('pointLedger.type.admin'),
      other: t('pointLedger.type.other'),
      loyalty: t('pointLedger.type.loyalty'),
    },
    title: t('pointLedger.type.DEFAULT'),
    group: t('pages.driver.pointList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => t(`pointLedger.type.${v}`),
    filter: {
      type: 'dropdown',
      group: t('pages.driver.pointList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/PointType', {}, (value) => ({
          value,
          label: t(`pointLedger.type.${value}`),
        })),
        label: t('pointLedger.type.DEFAULT'),
      },
    },
  },
  {
    key: 'pointReward.name',
    title: t('pointLedger.pointReward'),
    group: t('pages.driver.pointList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ?? '-',
    filter: {
      type: 'text',
      group: t('pages.driver.pointList.filterGroups.general'),
      options: {
        label: t('pointLedger.pointReward'),
      },
    },
    to: (item: PointLedger) =>
      item.pointReward?.id
        ? {
          name: 'point-reward-details',
          params: { pointRewardId: item.pointReward.id }
        }
        : undefined,
  },
  {
    key: 'interestArea.name',
    title: t('navigation.loyalty.interestZone'),
    group: t('pages.driver.pointList.fieldGroups.general'),
    sortable: true,
    filter: {
      type: 'dropdown',
      key: 'interestArea.id',
      group: t('pages.driver.pointList.filterGroups.general'),
      options: {
        options: collOptionsProvider<Company>('/interest_areas', (value) => ({
          label: value.name,
          value: value.id,
        })),
        label: t('navigation.loyalty.interestZone'),
      },
    },
  },
  {
    key: 'author.fullName',
    title: t('pointLedger.author.name'),
    group: t('pages.driver.pointList.fieldGroups.general'),
    sortable: true,
    visible: true,
    formatter: (v?: string) => v ?? '-',
    filter: {
      type: 'text',
      group: t('pages.driver.pointList.filterGroups.general'),
      options: {
        label: t('pointLedger.author.name'),
      },
    },
  },
  {
    key: 'comment',
    title: t('pointLedger.comment'),
    group: t('pages.driver.pointList.fieldGroups.general'),
    sortable: false,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.driver.pointList.filterGroups.general'),
      options: {
        label: t('pointLedger.comment'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const blockRequest = computed(() => !props.modelValue);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<PointLedger>>('/point_ledgers', providerParams, {
  disabled: blockRequest,
  immediate: true,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  })
);

const notify = useNotify();

const [exec, { loading: loadingDelete }] = useFetchAction();

const loading = computed(() => loadingTable.value || loadingDelete.value);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>

